import { toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameList, SerializationContext, SerializedData } from '../dto/dto.js';
import { HealthAndSafetyRule } from './health-and-safety-rule.js';

export class HealthAndSafetySettings extends DataModel {
  static readonly TYPE_ID: string = '7c039643a0164b86af1f9afaf5451d18';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly rules: HealthAndSafetyRule[],
    public readonly showRulesOnProfile: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return HealthAndSafetySettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(HealthAndSafetySettings.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): HealthAndSafetySettings {
    return new HealthAndSafetySettings(
        MapUtils.getList<HealthAndSafetyRule>(map, 'rules', (value) =>
          toEnumFromString(value as string, HealthAndSafetyRule)),
        MapUtils.get<boolean>(map, 'showRulesOnProfile'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'showRulesOnProfile': this.showRulesOnProfile,
      'rules': this.rules.map((r) => r),
    };
  }

  clone(): this {
    return new HealthAndSafetySettings(
        this.rules,
        this.showRulesOnProfile,
    ) as this;
  }

  isSame(other: HealthAndSafetySettings): boolean {
    if(other === this) {
      return true;
    }

    return other.showRulesOnProfile === this.showRulesOnProfile &&
      isSameList(other.rules, this.rules);
  }
}

export class HealthAndSafetySettingsBuilder extends DataModelBuilder<HealthAndSafetySettings> {
  build(context: SerializationContext, map: SerializedData): HealthAndSafetySettings {
    return HealthAndSafetySettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(HealthAndSafetySettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(HealthAndSafetySettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      const showRulesOnProfile: boolean = MapUtils.getOrNull<boolean>(map, 'allowPublicRules') ?? false;
      MapUtils.set(map, 'showRulesOnProfile', showRulesOnProfile);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
